import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button, EmptyState, ErrorState, Pager, PageHeader, SkeletonRow,
} from '@parsina/framework';
import { Building2, DownloadCloud } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { clinicsView, SORT_KEYS, TIER_FILTERS } from '../../lib/clinicsView';
import { STATUS_FILTERS, UNPROVISIONED } from '../../constants/subscription';
import { PAGE_SIZE } from '../../constants/list';
import ClinicsToolbar from './ClinicsToolbar';
import ClinicsTable from './ClinicsTable';
import BackfillDialog from './BackfillDialog';

/**
 * Archetype B (Report/List) — the estate.
 *
 * ① PageHeader · ④ toolbar · ⑤ table · ⑥ pager.
 *
 * ── THE VIEW IS THE URL ─────────────────────────────────────────────────────
 * Filters, search, sort and page are query params (§3.2). They were component
 * state, which broke the flow this screen exists for: an operator narrows to
 * «مهلت تمدید», opens a clinic, comes back — and every filter is gone. It also
 * makes a view sendable, which matters when two people are working one incident.
 *
 * ── THE SERVER IS NOT ASKED TO FILTER ───────────────────────────────────────
 * `clinics` is called with NO arguments. The BFF returns the whole estate anyway
 * (it joins the registry to one batched entitlement call — a per-clinic lookup
 * would be thousands of round trips) and its own `search`/`status` arguments
 * filter that same in-memory array. So a server-side filter costs a round trip
 * and returns a subset of what is already here. `clinicsView` does it locally and
 * is a pure, tested function; when the estate outgrows that, the fix is
 * `limit`/`offset` on the query, which is a BFF change.
 */
const CLINICS_QUERY = `
  query Clinics {
    clinics {
      id name clinicType officePhone active createdAt currency
      subscriptionStatus entitled hasSubscription planCode capabilitySet tierNameFa
      currentPeriodEnd trialEndsAt
    }
  }
`;

/** The URL contract. Defaults are absent from the address. */
const SCHEMA = {
  q: { default: '' },
  status: { default: 'ALL', values: STATUS_FILTERS },
  tier: { default: 'ALL', values: TIER_FILTERS },
  entitled: { default: '', values: ['', '0', '1'] },
  sort: { default: 'created', values: SORT_KEYS },
  dir: { default: 'desc', values: ['asc', 'desc'] },
  page: { default: 1, parse: (raw) => Number(raw) || 1 },
};

export default function Clinics() {
  const { t, locale, formatNumber } = useTranslation();
  const navigate = useNavigate();
  useDocumentTitle('nav.title.clinics');

  const [view, setView, resetView] = useUrlState(SCHEMA);
  const [rawParams, setRawParams] = useSearchParams();

  /**
   * `?unprovisioned=1` → `?status=UNPROVISIONED`.
   *
   * The old dashboard alert linked here with that param, and so do any bookmarks
   * and ticket notes made since. It is rewritten to the canonical one on arrival
   * (with `replace`, so it leaves no history entry) rather than being carried as a
   * second way to express one filter — two spellings of the same view is how they
   * drift apart.
   */
  React.useEffect(() => {
    if (!rawParams.get('unprovisioned')) return;
    const next = new URLSearchParams(rawParams);
    next.delete('unprovisioned');
    next.set('status', UNPROVISIONED);
    setRawParams(next, { replace: true });
  }, [rawParams, setRawParams]);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [backfilling, setBackfilling] = React.useState(false);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(CLINICS_QUERY)
      // Default to []: a malformed or unexpected response must not put
      // `undefined` into state and white-screen the panel.
      .then((d) => setRows(d?.clinics || []))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [t]);

  // Once. Every narrowing after this is local, so a filter change must NOT
  // refetch — that was the old behaviour and it made every chip press a round
  // trip that could fail.
  React.useEffect(() => { load(); }, [load]);

  const result = React.useMemo(
    () => clinicsView(rows, view, { locale }),
    [rows, view, locale],
  );

  // Counts for the chip row, from the UNFILTERED estate: a count that changed
  // with the active filter would be a count of what you are already looking at.
  const counts = React.useMemo(() => {
    const out = { ALL: rows.length };
    for (const key of STATUS_FILTERS) if (key !== 'ALL') out[key] = 0;
    for (const r of rows) {
      const key = r.hasSubscription ? r.subscriptionStatus : UNPROVISIONED;
      if (key in out) out[key] += 1;
    }
    return out;
  }, [rows]);

  const onSort = (key) => {
    // desc → asc → back to the default. A third press restoring the default is
    // what makes an accidental sort undoable without hunting for the original.
    if (view.sort !== key) setView({ sort: key, dir: 'desc', page: 1 });
    else if (view.dir === 'desc') setView({ dir: 'asc', page: 1 });
    else setView({ sort: SCHEMA.sort.default, dir: SCHEMA.dir.default, page: 1 });
  };

  const openClinic = (row) => {
    // Where the operator was, so Back returns to this exact view.
    try { sessionStorage.setItem('clinics.lastRow', row.id); } catch { /* storage off */ }
    navigate(`/clinics/${row.id}`);
  };

  const resultLabel = result.isFiltered
    ? t('clinics.countFiltered', { count: result.filtered, total: result.total })
    : t('clinics.count', { count: result.total });

  return (
    <div className="flex h-full flex-col bg-background" data-testid="clinics">
      <PageHeader title={t('clinics.title')} sub={t('clinics.sub')}>
        {/* The page's ONE primary action, and only when there is something for it
            to do. A permanently-visible provisioning button on a fully-provisioned
            estate invites a press that does nothing. */}
        {result.unprovisioned > 0 ? (
          <Button onClick={() => setBackfilling(true)} data-testid="clinics-backfill">
            <DownloadCloud size={16} aria-hidden />
            {t('clinics.backfill.trigger', { count: result.unprovisioned })}
          </Button>
        ) : null}
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <ClinicsToolbar
          view={view}
          counts={counts}
          isFiltered={result.isFiltered}
          resultLabel={resultLabel}
          onChange={setView}
          onClear={resetView}
        />

        <div
          className="rounded-lg border border-border bg-card transition-opacity"
          // Stale-while-refetch (§2.4): after a write the list dims rather than
          // being replaced by skeletons, so the operator keeps their place.
          style={{ opacity: refreshing ? 0.45 : 1 }}
          aria-busy={refreshing || undefined}
        >
          {loading ? (
            // Skeleton ROWS in the real table frame, so nothing moves when the
            // data lands. Two `SkeletonCard`s (the old loading state) were a
            // different shape from the thing they stood in for.
            <div data-testid="clinics-loading" data-state="loading" aria-busy="true">
              <table className="w-full">
                <tbody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SkeletonRow key={i} cols={8} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            // The error replaces exactly the region whose query failed, INSIDE the
            // card where the rows would be — the toolbar above it still works, so
            // a retry does not cost the operator their filters.
            <ErrorState
              title={t('errors.region.clinics')}
              description={error}
              onRetry={load}
              retryLabel={t('common.actions.retry')}
              data-testid="clinics-error"
            />
          ) : (
            <ClinicsTable
              rows={result.rows}
              view={view}
              onSort={onSort}
              onOpen={openClinic}
              empty={result.isFiltered ? (
                // Filtered-empty and truly-empty are different facts, and telling
                // an operator "no clinics exist" when four hundred do is a wrong
                // answer, not a vague one.
                <EmptyState
                  icon={<Building2 size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('clinics.emptyFiltered.title')}
                  description={t('clinics.emptyFiltered.desc')}
                  action={(
                    <Button variant="outline" onClick={resetView}>
                      {t('common.actions.clearFilters')}
                    </Button>
                  )}
                  data-testid="clinics-empty"
                />
              ) : (
                <EmptyState
                  icon={<Building2 size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('clinics.empty.title')}
                  description={t('clinics.empty.desc')}
                  data-testid="clinics-empty"
                />
              )}
            />
          )}
        </div>

        {/* Wrapped for the testid: the kit's `Pager` takes a fixed prop list and
            does not forward extras to its root, so a `data-testid` on it would be
            silently inert. */}
        {!loading && !error ? (
          <div data-testid="clinics-pager">
            <Pager
              page={result.page}
              pageCount={result.pageCount}
              pageSize={PAGE_SIZE}
              onChange={(page) => setView({ page })}
              prevLabel={t('common.pager.prev')}
              nextLabel={t('common.pager.next')}
              pageLabel={(page, total) => t('common.pager.label', { page, total })}
              formatNumber={formatNumber}
            />
          </div>
        ) : null}
      </div>

      <BackfillDialog
        open={backfilling}
        onOpenChange={setBackfilling}
        unprovisioned={result.unprovisioned}
        onDone={() => load({ quiet: true })}
      />
    </div>
  );
}
